import {
  CaretDownIcon,
  CaretUpDownIcon,
  CaretUpIcon,
} from "@phosphor-icons/react";
import { Link, type LinkProps } from "@tanstack/react-router";
import { cva, type VariantProps } from "class-variance-authority";
import React, {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  composeRenderProps,
} from "react-aria-components";
import { cn } from "@/lib/utils";

const buttonVariantAppearance = {
  default: ["bg-violet-800", "text-white"].join(" "),
  outline: [
    "border",
    "border-gray-200",
    "bg-background",
    "shadow-xs",
    "text-slate-800",
  ].join(" "),
} as const;

const buttonVariantAriaInteractions = {
  default: ["data-hovered:bg-violet-700", "data-pressed:bg-violet-900"].join(
    " ",
  ),
  outline: ["hover:bg-slate-400/5", "active:bg-slate-500/10"].join(" "),
} as const;

const buttonVariantLinkInteractions = {
  default: ["hover:bg-violet-700", "active:bg-violet-900"].join(" "),
  outline: ["hover:bg-slate-400/5", "active:bg-slate-500/10"].join(" "),
} as const;

const buttonSizeAppearance = {
  default: "h-10 px-4 py-2 text-sm font-medium",
  sm: "h-9 rounded-md min-w-[70px] px-3 text-xs font-normal",
} as const;

const buttonVariants = cva(
  [
    // Base styles
    [
      "items-center justify-center",
      "whitespace-nowrap rounded-md",
      "ring-offset-background",
      "transition-colors transition-duration-100",
      "cursor-pointer",
    ].join(" "),

    // State styles
    [
      /* Disabled */
      "data-disabled:pointer-events-none",
      "data-disabled:opacity-50",
      /* Focus */
      "data-focus:outline-hidden",
      "data-focus:ring-0",
      "data-focus:ring-offset-0",
      /* Focus Visible */
      "data-focus-visible:outline-hidden",
      "data-focus-visible:ring-2",
      "data-[focus-visible]:ring-ring",
      "data-focus-visible:ring-offset-2",
      "data-focus-visible:ring-blue-750",
      /* Reset styles */
      "!focus-visible:outline-hidden",
      "focus:outline-hidden",
    ].join(" "),
  ],
  {
    variants: {
      variant: {
        input: [
          "rounded-md",
          "bg-slate-400/15",
          "text-slate-500",
          "inline-flex",
        ].join(" "),

        default: [
          buttonVariantAppearance.default,
          buttonVariantAriaInteractions.default,
          "inline-flex",
        ].join(" "),

        destructive: [
          "border",
          "border-gray-200",
          "bg-background",
          "hover:bg-slate-400/5",
          "active:bg-slate-500/10",
          "shadow-xs",
          "text-slate-800",
          "inline-flex",
        ].join(" "),

        outline: [
          buttonVariantAppearance.outline,
          buttonVariantAriaInteractions.outline,
          "inline-flex",
        ].join(" "),

        secondary: ["bg-slate-100", "text-slate-700", "inline-flex"].join(" "),

        ghost: [
          "hover:bg-gray-100",
          "active:bg-gray-200/50",
          "text-gray-800",
          "data-hovered:bg-gray-100",
          "data-pressed:bg-gray-200/50",
          "inline-flex",
        ].join(" "),

        secondaryDestructive: [
          "bg-red-750",
          "text-white",
          "hover:bg-red-600",
          "active:bg-red-800",
          "data-hovered:bg-red-600",
          "data-pressed:bg-red-800",
          "inline-flex",
        ].join(" "),

        none: "text-left",

        link: [
          "text-gray-900",
          "underline",
          // "underline-offset-2",
          "data-hovered:underline",
          "flex",
        ].join(" "),
      },

      size: {
        default: buttonSizeAppearance.default,
        input: [
          "py-1 px-2",
          "absolute h-6",
          "top-1/2 -translate-y-1/2",
          "absolute right-2.5",
          "text-xs",
          "font-medium",
        ].join(" "),
        sm: buttonSizeAppearance.sm,
        lg: "h-11 rounded-md px-8 text-sm font-medium",
        icon: "size-9 text-sm font-medium",
        xsIcon: "size-5 text-xs font-medium",
        none: "inline-block font-medium",
        label: "p-0 h-auto font-medium",
        link: "p-0 h-auto text-xxs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const linkButtonVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap rounded-md",
    "ring-offset-background transition-colors transition-duration-100 cursor-pointer no-underline",
    "focus:outline-hidden focus-visible:outline-hidden focus-visible:ring-2",
    "focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-blue-750",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          buttonVariantAppearance.default,
          buttonVariantLinkInteractions.default,
        ].join(" "),

        outline: [
          buttonVariantAppearance.outline,
          buttonVariantLinkInteractions.outline,
        ].join(" "),
      },

      size: {
        default: buttonSizeAppearance.default,
        sm: buttonSizeAppearance.sm,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface ButtonProps
  extends AriaButtonProps,
    VariantProps<typeof buttonVariants> {
  type?: "button" | "submit" | "reset";
}

export type ButtonSizeProps = VariantProps<typeof buttonVariants>["size"];

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => {
    return (
      <AriaButton
        ref={ref}
        type={type}
        className={composeRenderProps(className, (className) =>
          cn(
            buttonVariants({
              variant,
              size,
            }),
            className,
          ),
        )}
        {...props}
      />
    );
  },
);

interface DropdownButtonProps extends ButtonProps {
  ariaLabel: string;
  icon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  label: string;
  customLabel?: React.ReactNode;
  dropdownVariant?: "primary" | "secondary" | "ghost";
  /** Renders a vertical divider before the caret. */
  showDivider?: boolean;
}

const DropdownButton = React.forwardRef<HTMLButtonElement, DropdownButtonProps>(
  (
    {
      ariaLabel,
      icon,
      trailingIcon,
      label,
      customLabel,
      dropdownVariant = "primary",
      showDivider = false,

      ...props
    },
    ref,
  ) => {
    const isSecondary = dropdownVariant === "secondary";
    const isGhost = dropdownVariant === "ghost";

    return (
      <Button
        ref={ref}
        aria-label={ariaLabel}
        size="sm"
        variant="outline"
        className={cn("max-w-full px-2 gap-1 bg-white", {
          "text-gray-900 text-xs font-normal": isSecondary,
          "text-gray-700": !isSecondary,
        })}
        {...props}
      >
        {icon}
        <span
          className={cn(
            "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap pl-1",
            { "text-gray-900": isSecondary },
            { "text-gray-700": !isSecondary },
          )}
        >
          {customLabel || label}
        </span>
        {trailingIcon}
        {showDivider && !isGhost ? (
          <span className="mx-1 h-6 w-px shrink-0 bg-gray-200" aria-hidden />
        ) : null}
        {isGhost ? (
          <span className="pr-4 flex shrink-0" />
        ) : isSecondary ? (
          <CaretDownIcon className="ml-1 shrink-0 fill-gray-700" size={16} />
        ) : (
          <CaretUpDownIcon className="ml-1 shrink-0 fill-gray-700" size={16} />
        )}
      </Button>
    );
  },
);

interface DropdownWithSubLabelButtonProps extends ButtonProps {
  ariaLabel: string;
  icon?: React.ReactNode;
  label: string;
  customLabel?: React.ReactNode;
  buttonTitle?: React.ReactNode;
  buttonSize?: ButtonSizeProps;
  className?: string;
  dropdownVariant?: "primary" | "secondary" | "ghost";
}

const DropdownWithSubLabelButton = React.forwardRef<
  HTMLButtonElement,
  DropdownWithSubLabelButtonProps
>(
  (
    {
      ariaLabel,
      icon,
      label,
      customLabel,
      buttonTitle,
      buttonSize = "sm",
      className,
      dropdownVariant = "primary",
      ...props
    },
    ref,
  ) => {
    const isSecondary = dropdownVariant === "secondary";
    const isGhost = dropdownVariant === "ghost";

    return (
      <Button
        ref={ref}
        aria-label={ariaLabel}
        size={buttonSize}
        variant="outline"
        className={cn("px-2 text-gray-700 text-xs font-normal", className)}
        {...props}
      >
        <div
          className={cn(
            "flex flex-row items-center gap-1.5 w-full pl-1 min-w-0",
          )}
        >
          <span className={cn("text-gray-600 text-xxs shrink-0")}>
            {buttonTitle}:
          </span>
          {icon}
          <span
            className={cn(
              "text-gray-900 text-xs overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1",
            )}
          >
            {customLabel || label}
          </span>
        </div>

        {isGhost ? (
          <span className="pr-4 flex shrink-0" />
        ) : isSecondary ? (
          <CaretDownIcon className="ml-1 shrink-0 fill-gray-700" size={16} />
        ) : (
          <CaretUpDownIcon className="ml-1 shrink-0 fill-gray-700" size={16} />
        )}
      </Button>
    );
  },
);

// Button Group
interface ButtonConfig {
  /**
   * label: The label of the button
   */
  label: string;
  /**
   * onClick: The function to be called when the button is clicked
   */
  onClick: () => void;
}

interface ButtonGroupProps {
  /**
   * buttonConfig: An array of button configurations consisting of the label and onClick function
   */
  buttonConfig: ButtonConfig[];
  /**
   * defaultSelectedIndex: The index of the button to be selected by default
   */
  defaultSelectedIndex?: number;
  /**
   * className: The class name to be applied to the button group
   */
  className?: string;
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({
  buttonConfig,
  className,
  defaultSelectedIndex,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(
    defaultSelectedIndex ?? 0,
  );

  useEffect(() => {
    if (defaultSelectedIndex !== undefined) {
      setSelectedIndex(defaultSelectedIndex);
    }
  }, [defaultSelectedIndex]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLFieldSetElement>) => {
      if (event.key === "ArrowRight") {
        setSelectedIndex((prevIndex) => (prevIndex + 1) % buttonConfig.length);
      } else if (event.key === "ArrowLeft") {
        setSelectedIndex((prevIndex) =>
          prevIndex === 0 ? buttonConfig.length - 1 : prevIndex - 1,
        );
      }
    },
    [buttonConfig.length],
  );

  return (
    <fieldset
      aria-label="Button group"
      onKeyDown={handleKeyDown}
      className={cn(
        "flex text-xxs tracking-wide font-normal cursor-pointer shadow-xs rounded-lg",
        className,
      )}
    >
      {buttonConfig.map((button, index) => (
        <AriaButton
          key={button.label}
          onPress={() => {
            button.onClick();
            setSelectedIndex(index);
          }}
          className={cn("border px-3 py-1 min-h-[30px]", {
            "rounded-l-md border-r-0": index === 0,
            "rounded-r-md border-l-0": index === buttonConfig.length - 1,
            "bg-violet-100 font-medium hover:bg-violet-200":
              index === selectedIndex,
            "bg-white font-normal hover:bg-gray-100": index !== selectedIndex,
          })}
          onFocus={() => setSelectedIndex(index)}
        >
          {button.label}
        </AriaButton>
      ))}
    </fieldset>
  );
};

const tableButtonSizeMap = {
  sm: "text-xxs",
  md: "text-xs",
  lg: "text-sm",
} as const;

type TableButtonSize = keyof typeof tableButtonSizeMap;

interface TableButtonProps extends Omit<AriaButtonProps, "className"> {
  /** Font size variant. sm→xxs, md→xs, lg→sm */
  size?: TableButtonSize;
  /** Tailwind background class, e.g. `bg-gray-900` or `bg-violet-950` */
  backgroundClassName: string;
  /** Optional text color class, defaults to `text-white` */
  textClassName?: string;
  /** Optional additional class names */
  className?: string;
  type?: "button" | "submit" | "reset";
}

/**
 * Compact table button with height driven by text.
 * Size variants map to font sizes: sm→xxs, md→xs, lg→sm.
 */
const tableButtonBaseStyles =
  "flex items-center justify-center gap-1 rounded-sm px-2 py-0 my-0";

const TableButton = React.forwardRef<HTMLButtonElement, TableButtonProps>(
  (
    {
      size = "md",
      backgroundClassName,
      textClassName = "text-white",
      className,
      type = "button",
      ...props
    },
    ref,
  ) => (
    <AriaButton
      ref={ref}
      type={type}
      className={composeRenderProps(className, (resolvedClassName) =>
        cn(
          tableButtonBaseStyles,
          tableButtonSizeMap[size],
          backgroundClassName,
          textClassName,
          resolvedClassName,
        ),
      )}
      {...props}
    />
  ),
);

const showMoreButtonClassName =
  "text-[10px] font-medium text-gray-500 flex items-center gap-1 px-0 h-8 hover:text-gray-700 underline-offset-4 underline";

interface ShowMoreButtonProps {
  isExpanded: boolean;
  onToggle: () => void;
}

const ShowMoreButton = ({ isExpanded, onToggle }: ShowMoreButtonProps) => (
  <Button
    variant="none"
    size="sm"
    onPress={onToggle}
    className={showMoreButtonClassName}
  >
    {isExpanded ? (
      <CaretUpIcon size={12} aria-hidden />
    ) : (
      <CaretDownIcon size={12} aria-hidden />
    )}
    {isExpanded ? "Show Less" : "Show More"}
  </Button>
);

interface LinkButtonProps
  extends LinkProps,
    VariantProps<typeof linkButtonVariants> {
  className?: string;
}

const LinkButton = ({
  className,
  variant,
  size,
  ...props
}: LinkButtonProps) => (
  <Link
    className={cn(linkButtonVariants({ variant, size }), className)}
    {...props}
  />
);

export type {
  ButtonGroupProps,
  ButtonProps,
  LinkButtonProps,
  ShowMoreButtonProps,
  TableButtonProps,
};
export {
  Button,
  ButtonGroup,
  buttonVariants,
  DropdownButton,
  DropdownWithSubLabelButton,
  LinkButton,
  ShowMoreButton,
  TableButton,
};
